import { useRef, useState } from "react";
import { MapPin, Phone, Mail, Globe, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { CONTACT, EMAILJS_CONFIG } from "../lib/content";

export default function Contact() {
  const formRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    asunto: "",
    mensaje: "",
  });

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;

    setStatus("sending");
    setErrorMsg("");

    // Direct REST call to EmailJS — avoids @emailjs/browser body-stream bug
    // and surfaces the real server error message.
    try {
      const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: EMAILJS_CONFIG.SERVICE_ID,
          template_id: EMAILJS_CONFIG.TEMPLATE_ID,
          user_id: EMAILJS_CONFIG.PUBLIC_KEY,
          template_params: {
            nombre: form.nombre,
            email: form.email,
            telefono: form.telefono,
            asunto: form.asunto,
            mensaje: form.mensaje,
            // Common aliases so different EmailJS templates work out of the box
            from_name: form.nombre,
            from_email: form.email,
            reply_to: form.email,
            phone: form.telefono,
            subject: form.asunto,
            message: form.mensaje,
            to_email: CONTACT.email,
          },
        }),
      });

      if (!res.ok) {
        const body = await res.text().catch(() => "");
        let userMsg;
        if (res.status === 400) {
          userMsg = body
            ? `EmailJS rechazó la solicitud (400): ${body}`
            : "EmailJS rechazó la solicitud (400). Revisa que las variables de la plantilla coincidan con los campos enviados.";
        } else if (res.status === 403) {
          userMsg =
            "Dominio no autorizado en EmailJS (403). Agrega este dominio en EmailJS → Account → Security → Allowed Origins.";
        } else if (res.status === 404) {
          userMsg = "Service ID o Template ID no encontrado (404). Verifica los IDs en tu cuenta de EmailJS.";
        } else {
          userMsg = `Error de EmailJS (${res.status}). ${body || "Intenta de nuevo."}`;
        }
        throw new Error(userMsg);
      }

      setStatus("success");
      setForm({ nombre: "", email: "", telefono: "", asunto: "", mensaje: "" });
      setTimeout(() => setStatus("idle"), 6000);
    } catch (err) {
      console.error("EmailJS error:", err);
      setErrorMsg(err?.message || "No se pudo enviar el mensaje. Intenta de nuevo.");
      setStatus("error");
      setTimeout(() => setStatus("idle"), 9000);
    }
  };

  const inputBase =
    "w-full bg-transparent border-b border-[var(--ms-border)] focus:border-[var(--ms-purple)] outline-none px-0 py-3.5 font-body text-[15px] text-[var(--ms-ink)] placeholder:text-[var(--ms-gray)]/70 transition-colors duration-300";

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-[#FCFAFF] py-24 md:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-center gap-3 mb-10">
          <span className="font-body uppercase text-[11px] tracking-[0.32em] text-[var(--ms-purple)] font-bold">
            06 / Contacto
          </span>
          <span className="flex-1 h-px bg-[var(--ms-border)]" />
        </div>

        <div className="grid grid-cols-12 gap-8 lg:gap-16">
          {/* Left - heading + form */}
          <div className="col-span-12 lg:col-span-7">
            <h2
              data-testid="contact-heading"
              className="font-display font-medium text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[var(--ms-ink)] mb-6"
            >
              Hablemos de tu{" "}
              <span className="font-editorial italic font-light text-[var(--ms-purple)]">
                marca.
              </span>
            </h2>
            <p className="font-body text-base md:text-lg leading-[1.7] text-[var(--ms-gray)] mb-10 md:mb-14 max-w-xl">
              Cuéntanos tu proyecto. Respondemos en menos de 24 horas con una propuesta personalizada y sin compromisos.
            </p>

            <form
              ref={formRef}
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
            >
              <div className="md:col-span-1">
                <label className="font-body text-[11px] uppercase tracking-[0.2em] text-[var(--ms-gray)] mb-1 block">
                  Nombre
                </label>
                <input
                  data-testid="contact-input-nombre"
                  type="text"
                  name="nombre"
                  required
                  value={form.nombre}
                  onChange={onChange}
                  placeholder="Tu nombre completo"
                  className={inputBase}
                />
              </div>

              <div className="md:col-span-1">
                <label className="font-body text-[11px] uppercase tracking-[0.2em] text-[var(--ms-gray)] mb-1 block">
                  Email
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  placeholder="tucorreo@ejemplo.com"
                  className={inputBase}
                />
              </div>

              <div className="md:col-span-1 mt-4">
                <label className="font-body text-[11px] uppercase tracking-[0.2em] text-[var(--ms-gray)] mb-1 block">
                  Teléfono
                </label>
                <input
                  data-testid="contact-input-telefono"
                  type="tel"
                  name="telefono"
                  value={form.telefono}
                  onChange={onChange}
                  placeholder="+52 33 0000 0000"
                  className={inputBase}
                />
              </div>

              <div className="md:col-span-1 mt-4">
                <label className="font-body text-[11px] uppercase tracking-[0.2em] text-[var(--ms-gray)] mb-1 block">
                  Asunto
                </label>
                <input
                  data-testid="contact-input-asunto"
                  type="text"
                  name="asunto"
                  required
                  value={form.asunto}
                  onChange={onChange}
                  placeholder="¿De qué quieres hablar?"
                  className={inputBase}
                />
              </div>

              <div className="md:col-span-2 mt-4">
                <label className="font-body text-[11px] uppercase tracking-[0.2em] text-[var(--ms-gray)] mb-1 block">
                  Mensaje
                </label>
                <textarea
                  data-testid="contact-input-mensaje"
                  name="mensaje"
                  rows={4}
                  required
                  value={form.mensaje}
                  onChange={onChange}
                  placeholder="Cuéntanos sobre tu marca y objetivos..."
                  className={`${inputBase} resize-none`}
                />
              </div>

              <div className="md:col-span-2 mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={status === "sending"}
                  className="group inline-flex items-center justify-between gap-6 px-7 py-4 rounded-full bg-[var(--ms-ink)] text-white font-body text-[14px] tracking-wide hover:bg-[var(--ms-purple)] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-500"
                >
                  <span>
                    {status === "sending" ? "Enviando…" : "Enviar mensaje"}
                  </span>
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-white/15 group-hover:rotate-[-12deg] transition-transform duration-500">
                    <Send size={14} />
                  </span>
                </button>

                {status === "success" && (
                  <div
                    data-testid="contact-success"
                    className="inline-flex items-center gap-2 text-[var(--ms-purple)] font-body text-sm"
                  >
                    <CheckCircle2 size={18} />
                    ¡Mensaje enviado! Te contactaremos pronto.
                  </div>
                )}

                {status === "error" && (
                  <div
                    data-testid="contact-error"
                    className="inline-flex items-center gap-2 text-red-600 font-body text-sm"
                  >
                    <AlertCircle size={18} />
                    {errorMsg}
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Right - contact info */}
          <aside className="col-span-12 lg:col-span-5">
            <div
              data-testid="contact-info-card"
              className="relative h-full p-8 md:p-10 rounded-3xl bg-[var(--ms-ink)] text-white overflow-hidden grain"
            >
              <div className="pointer-events-none absolute -top-32 -right-32 w-[360px] h-[360px] rounded-full blur-3xl opacity-30"
                   style={{ background: "radial-gradient(circle, #7030A0 0%, transparent 70%)" }} />

              <div className="relative">
                <span className="font-body uppercase text-[10px] tracking-[0.32em] text-[var(--ms-lila)]">
                  Información directa
                </span>
                <h3 className="font-display font-medium text-3xl md:text-4xl mt-4 mb-10 tracking-tight">
                  Encuéntranos.
                </h3>

                <ul className="flex flex-col gap-7">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/8 border border-white/10 flex items-center justify-center shrink-0">
                      <MapPin size={16} className="text-[var(--ms-lila)]" />
                    </div>
                    <div>
                      <div className="font-body text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">
                        Dirección
                      </div>
                      <p data-testid="contact-direccion" className="font-body text-[15px] leading-relaxed text-white/90">
                        {CONTACT.direccion}
                      </p>
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/8 border border-white/10 flex items-center justify-center shrink-0">
                      <Phone size={16} className="text-[var(--ms-lila)]" />
                    </div>
                    <div>
                      <div className="font-body text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">
                        Teléfono
                      </div>
                      <a
                        data-testid="contact-telefono"
                        href={`tel:${CONTACT.telefonoTel}`}
                        className="font-body text-[15px] text-white/90 hover:text-[var(--ms-lila)] transition"
                      >
                        {CONTACT.telefono}
                      </a>
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/8 border border-white/10 flex items-center justify-center shrink-0">
                      <Mail size={16} className="text-[var(--ms-lila)]" />
                    </div>
                    <div>
                      <div className="font-body text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">
                        Email
                      </div>
                      <a
                        data-testid="contact-email"
                        href={`mailto:${CONTACT.email}`}
                        className="font-body text-[15px] text-white/90 hover:text-[var(--ms-lila)] transition"
                      >
                        {CONTACT.email}
                      </a>
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/8 border border-white/10 flex items-center justify-center shrink-0">
                      <Globe size={16} className="text-[var(--ms-lila)]" />
                    </div>
                    <div>
                      <div className="font-body text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">
                        Página Web
                      </div>
                      <span data-testid="contact-web" className="font-body text-[15px] text-white/90">
                        {CONTACT.web}
                      </span>
                    </div>
                  </li>
                </ul>

                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="font-editorial italic text-xl leading-snug text-white/80">
                    "Cada idea merece una ejecución sublime."
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
